import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Search, Trash2, AlertTriangle, CheckCircle, Clock, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface OrphanedUser {
  user_id: string;
  user_email: string;
  user_created_at: string;
  has_related_data: boolean;
}

interface CronStatus {
  exists: boolean;
  schedule: string;
  active: boolean;
  next_run: string;
  last_run?: {
    last_run_at: string;
    users_deleted: number;
    trigger: string;
    execution_time_ms: number;
  };
}

export const OrphanedUserScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [settingUpCron, setSettingUpCron] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [orphanedUsers, setOrphanedUsers] = useState<OrphanedUser[]>([]);
  const [lastScanTime, setLastScanTime] = useState<Date | null>(null);
  const [cronStatus, setCronStatus] = useState<CronStatus | null>(null);
  const { toast } = useToast();

  // Load cron status on mount
  useEffect(() => {
    loadCronStatus();
  }, []);

  const loadCronStatus = async () => {
    try {
      setLoadingStatus(true);
      const { data, error } = await supabase.rpc('get_orphaned_cleanup_cron_status') as any;
      
      if (error) throw error;
      
      if (data?.cron_job) {
        setCronStatus({
          exists: data.cron_job.exists,
          schedule: data.cron_job.schedule || '0 2 * * *',
          active: data.cron_job.active,
          next_run: 'Daily at 2:00 AM',
          last_run: data.last_run?.last_run_at ? data.last_run : undefined
        });
      }
    } catch (error: any) {
      console.error('Failed to load cron status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const scanForOrphanedUsers = async () => {
    setScanning(true);
    try {
      const { data, error } = await supabase.rpc('find_orphaned_users' as any) as any;

      if (error) throw error;

      setOrphanedUsers(data || []);
      setLastScanTime(new Date());
      
      toast({
        title: "Scan Complete",
        description: `Found ${data?.length || 0} orphaned users`,
      });
    } catch (error: any) {
      console.error('Scan error:', error);
      toast({
        title: "Scan Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setScanning(false);
    }
  };

  const cleanupOrphanedUsers = async () => {
    if (orphanedUsers.length === 0) {
      toast({
        title: "No Users to Clean",
        description: "Scan for orphaned users first",
        variant: "destructive"
      });
      return;
    }

    setCleaning(true);
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-orphaned-users', {
        body: { trigger: 'manual' }
      }) as any;

      if (error) throw error;

      if (data?.success) {
        const executionTime = data.execution_time_ms || 0;
        toast({
          title: "Cleanup Complete",
          description: `${data.deleted_count} orphaned users removed in ${executionTime}ms`,
        });
        setOrphanedUsers([]);
        setLastScanTime(new Date());
      } else {
        throw new Error(data?.error || 'Cleanup failed');
      }
    } catch (error: any) {
      console.error('Cleanup error:', error);
      toast({
        title: "Cleanup Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCleaning(false);
    }
  };

  const setupAutoCron = async () => {
    setSettingUpCron(true);
    try {
      const { data, error } = await supabase.functions.invoke('setup-orphaned-cleanup-cron') as any;

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Auto-Cleanup Configured",
          description: `Cron job will run daily at 2:00 AM. Initial cleanup deleted ${data.initial_cleanup?.deleted_count || 0} users.`,
        });

        // Reload cron status
        await loadCronStatus();
        
        // Refresh scan to show current state
        await scanForOrphanedUsers();
      } else {
        throw new Error(data?.error || 'Setup failed');
      }
    } catch (error: any) {
      console.error('Setup error:', error);
      toast({
        title: "Setup Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSettingUpCron(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Orphaned User Scanner
          </span>
          {!loadingStatus && cronStatus?.exists && cronStatus.active && (
            <Badge variant="default" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Auto-Cleanup Active
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Detect and clean up users that exist in authentication but not in profiles
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cron Status Section */}
        {!loadingStatus && (
          <Alert className={cronStatus?.exists && cronStatus.active ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950" : "border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950"}>
            <Clock className={`h-4 w-4 ${cronStatus?.exists && cronStatus.active ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}`} />
            <AlertDescription>
              {cronStatus?.exists && cronStatus.active ? (
                <div className="space-y-1">
                  <p className="font-medium text-green-900 dark:text-green-100">Automatic cleanup is enabled</p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Runs {cronStatus.next_run} • Schedule: {cronStatus.schedule}
                  </p>
                  {cronStatus.last_run && (
                    <p className="text-xs text-green-600 dark:text-green-400">
                      Last run: {new Date(cronStatus.last_run.last_run_at).toLocaleString()} 
                      ({cronStatus.last_run.users_deleted} users deleted in {cronStatus.last_run.execution_time_ms}ms)
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="font-medium text-orange-900 dark:text-orange-100">Automatic cleanup is not configured</p>
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    Set up scheduled cleanup to automatically remove orphaned users daily at 2:00 AM
                  </p>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button onClick={scanForOrphanedUsers} disabled={scanning || cleaning} variant="outline">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Scan for Orphaned Users
          </Button>

          {(!cronStatus?.exists || !cronStatus?.active) && (
            <Button 
              onClick={setupAutoCron} 
              disabled={settingUpCron || scanning || cleaning}
              variant="default"
            >
              {settingUpCron ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Setting Up...
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  Setup Auto-Cleanup
                </>
              )}
            </Button>
          )}
          
          {orphanedUsers.length > 0 && (
            <Button 
              onClick={cleanupOrphanedUsers} 
              disabled={cleaning || scanning}
              variant="destructive"
            >
              {cleaning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Clean Up All ({orphanedUsers.length})
            </Button>
          )}
        </div>

        {lastScanTime && (
          <p className="text-sm text-muted-foreground">
            Last scan: {lastScanTime.toLocaleString()}
          </p>
        )}

        {orphanedUsers.length > 0 ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Orphaned Users Found</AlertTitle>
            <AlertDescription>
              {orphanedUsers.length} user(s) exist in authentication but not in profiles. 
              These users cannot log in and should be cleaned up.
            </AlertDescription>
          </Alert>
        ) : lastScanTime ? (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>All Clear</AlertTitle>
            <AlertDescription>
              No orphaned users found. Your database is clean!
            </AlertDescription>
          </Alert>
        ) : null}

        {orphanedUsers.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm">Orphaned Users:</h4>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {orphanedUsers.map((user) => (
                <div 
                  key={user.user_id}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                >
                  <div className="flex-1">
                    <p className="font-medium">{user.user_email}</p>
                    <p className="text-xs text-muted-foreground">
                      Created: {new Date(user.user_created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {user.has_related_data && (
                      <Badge variant="outline" className="text-xs">
                        Has Data
                      </Badge>
                    )}
                    <Badge variant="secondary" className="text-xs">
                      Orphaned
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
